import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CashfreeService } from './cashfree.service';
import {
  RazorpayOrderResponse,
  RazorpayPaymentResult,
  RazorpayService,
} from './razorpay.service';
import { PaymentGatewayType } from '../interfaces/settings.interface';

/**
 * PaymentGatewayService
 *
 * Facade service that delegates payment operations to either
 * Razorpay or Cashfree based on the `paymentGateway` feature setting.
 *
 * Default: 'cashfree' (backward compatible)
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentGatewayService {
  private readonly _gateway$ = new BehaviorSubject<PaymentGatewayType>(
    'cashfree',
  );

  /** Observable of the current gateway type */
  readonly gateway$ = this._gateway$.asObservable();

  constructor(
    private readonly cashfreeService: CashfreeService,
    private readonly razorpayService: RazorpayService,
  ) {}

  /** Current active gateway */
  get activeGateway(): PaymentGatewayType {
    return this._gateway$.value;
  }

  /** Set the active payment gateway (called when featureSettings are loaded) */
  setGateway(gateway: PaymentGatewayType): void {
    this._gateway$.next(gateway);
  }

  /** Whether Razorpay is the active gateway */
  get isRazorpay(): boolean {
    return this._gateway$.value === 'razorpay';
  }

  /** Whether Cashfree is the active gateway */
  get isCashfree(): boolean {
    return this._gateway$.value === 'cashfree';
  }

  /**
   * Initiate payment checkout.
   *
   * For Cashfree: redirects to Cashfree hosted page
   * For Razorpay: opens Razorpay modal and calls onSuccess/onDismiss callbacks
   */
  checkout(params: {
    // Cashfree params
    sessionId?: string;
    returnUrl?: string;
    amount: number;
    page: string;
    // Razorpay params
    razorpayOrderData?: RazorpayOrderResponse;
    onRazorpaySuccess?: (result: RazorpayPaymentResult) => void;
    onRazorpayDismiss?: () => void;
  }): void {
    if (this.isRazorpay && params.razorpayOrderData) {
      this.razorpayService.openCheckout(
        params.razorpayOrderData,
        params.returnUrl ?? '',
        params.page,
        params.onRazorpaySuccess ?? (() => {}),
        params.onRazorpayDismiss,
      );
    } else if (params.sessionId && params.returnUrl) {
      this.cashfreeService.cashfreeRedirection(
        params.sessionId,
        params.returnUrl,
        params.amount,
        params.page,
      );
    }
  }

  /**
   * Get payment/order details (same endpoint for both gateways)
   */
  getPaymentDetails(orderId: string): Observable<any> {
    return this.cashfreeService.getPaymentDetails(orderId);
  }
}
